'use client';

import type { ReactNode } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTrigger } from '@/app/components/ui/dialog';
import { cn } from '@/app/utils';

interface AudioSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactNode;
  header: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  triggerAsChild?: boolean;
  contentClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  closeClassName?: string;
  disableOverlayClose?: boolean;
}

/**
 * Shared shell for audio selection dialogs.
 * Keeps trigger, modal layout and footer consistent while letting each scene own its data source.
 */
export function AudioSelectionDialog({
  open,
  onOpenChange,
  trigger,
  header,
  footer,
  children,
  triggerAsChild = false,
  contentClassName,
  bodyClassName,
  footerClassName,
  closeClassName,
  disableOverlayClose = false,
}: AudioSelectionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild={triggerAsChild}>{trigger}</DialogTrigger>
      <DialogContent
        className={cn(
          'z-[60] flex h-[500px] w-[min(800px,calc(100vw-32px))] max-w-[min(800px,calc(100vw-32px))] flex-col gap-0 overflow-hidden rounded-16 border-system-white16 bg-[rgba(34,37,42,0.64)] px-0 pt-4 pb-0 backdrop-blur-[15px] [box-shadow:0_10px_20px_0_rgba(0,0,0,0.08)] sm:h-[min(560px,calc(100dvh-32px))] sm:w-[calc(100vw-32px)] sm:max-w-[calc(100vw-32px)]',
          contentClassName,
        )}
        overlayClassname="z-[60]"
        closeClassName={closeClassName ?? 'top-4 right-4'}
        disableOverlayClose={disableOverlayClose}
      >
        <DialogHeader>{header}</DialogHeader>
        <div className={cn('flex min-h-0 flex-1 flex-col', bodyClassName)}>{children}</div>
        {footer ? <DialogFooter className={cn('h-[68px] px-8 py-4', footerClassName)}>{footer}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  );
}
